import { type SubmitEvent, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { env } from "@platform/core/env";

import { useSearch } from "@tanstack/react-router";
import { useTranslations } from "use-intl";

import { Button } from "@lawline/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@lawline/ui/components/card";
import { Input } from "@lawline/ui/components/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@lawline/ui/components/input-otp";
import { Tooltip, TooltipContent, TooltipTrigger } from "@lawline/ui/components/tooltip";

import { authClient } from "@platform/auth/client";
import { oauthRedirect } from "@platform/core/oauth-redirect";
import googleLogo from "@platform/assets/google.svg";
import microsoftLogo from "@platform/assets/microsoft.svg";

export const DEFAULT_DEST = "/";
const loginMethodsSchema = z.object({
	email: z.boolean(),
	google: z.boolean(),
	microsoft: z.boolean(),
});

export function safeDest(redirect: string | undefined, origin: string): string {
	if (!redirect || !/^\/(?![/\\])/.test(redirect)) return DEFAULT_DEST;
	try {
		// Browsers remove tabs and line breaks before resolving the host.
		return new URL(redirect, origin).origin === origin ? redirect : DEFAULT_DEST;
	} catch {
		return DEFAULT_DEST;
	}
}

// Email-OTP and social both auto-register. Social redirects. OTP is a two-step
// flow, request code then verify, toggled by `step`.
export function LoginPage() {
	const t = useTranslations("auth.login");
	const { redirect, error: callbackError } = useSearch({ from: "/login" });
	const dest = safeDest(redirect, window.location.origin);
	const methods = useQuery({
		queryKey: ["login-methods"],
		retry: false,
		queryFn: async ({ signal }) => {
			const response = await fetch(`${env.API_BASE_URL}/platform/login-methods`, {
				credentials: "omit",
				cache: "no-store",
				signal: AbortSignal.any([signal, AbortSignal.timeout(15_000)]),
			});
			if (!response.ok) throw new Error("Could not load sign-in methods");
			return loginMethodsSchema.parse(await response.json());
		},
	});

	const [step, setStep] = useState<"email" | "otp">("email");
	const [email, setEmail] = useState("");
	const [otp, setOtp] = useState("");
	const [pending, setPending] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [cooldown, setCooldown] = useState(0);
	const inFlight = useRef(false);
	const fetchOptions = () => ({ signal: AbortSignal.timeout(15_000), retry: 0 }) as const;

	useEffect(() => {
		if (cooldown <= 0) return;
		const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
		return () => clearTimeout(t);
	}, [cooldown]);

	async function submit(
		action: () => Promise<{ error?: unknown; data?: unknown }>,
		failure: string,
		onSuccess: (data?: unknown) => void = () => {},
	) {
		if (inFlight.current) return;
		inFlight.current = true;
		setPending(true);
		setError(null);
		try {
			const result = await action();
			if (result.error) setError(failure);
			else onSuccess(result.data);
		} catch {
			setError(t("networkError"));
		} finally {
			inFlight.current = false;
			setPending(false);
		}
	}

	function social(provider: "google" | "microsoft") {
		if (!methods.data?.[provider]) return;
		const errorURL = new URL("/login", window.location.origin);
		errorURL.searchParams.set("redirect", dest);
		return submit(
			() =>
				authClient.signIn.social({
					provider,
					callbackURL: `${window.location.origin}${dest}`,
					errorCallbackURL: errorURL.toString(),
					fetchOptions: fetchOptions(),
				}),
			t("socialError"),
		);
	}

	function sendCode(e?: SubmitEvent) {
		e?.preventDefault();
		if (!methods.data?.email) return;
		return submit(
			() =>
				authClient.emailOtp.sendVerificationOtp({
					email,
					type: "sign-in",
					fetchOptions: fetchOptions(),
				}),
			t("sendError"),
			() => {
				setCooldown(30);
				setStep("otp");
			},
		);
	}

	function verify(e?: SubmitEvent) {
		e?.preventDefault();
		if (otp.length !== 6) return;
		return submit(
			() => authClient.signIn.emailOtp({ email, otp, fetchOptions: fetchOptions() }),
			t("invalidCode"),
			(data) => {
				window.location.href = oauthRedirect(data) ?? dest;
			},
		);
	}

	return (
		<div className="flex min-h-screen items-center justify-center p-6">
			<Card className="w-full max-w-sm">
				{step === "email" ? (
					<>
						<CardHeader className="text-center">
							<CardTitle className="text-2xl">{t("title")}</CardTitle>
						</CardHeader>
						<CardContent className="flex flex-col gap-4">
							{methods.isPending ? <p role="status">{t("loadingMethods")}</p> : null}
							{methods.isError ? (
								<div role="alert">
									<p>{t("methodsError")}</p>
									<Button variant="outline" onClick={() => methods.refetch()}>
										{t("retry")}
									</Button>
								</div>
							) : null}
							{methods.data && !Object.values(methods.data).some(Boolean) ? (
								<p role="alert">{t("unavailable")}</p>
							) : null}
							{methods.data?.email ? (
								<form onSubmit={sendCode} className="flex flex-col gap-3">
									<Input
										type="email"
										disabled={pending}
										autoComplete="email"
										placeholder={t("emailPlaceholder")}
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										required
									/>
									<Button type="submit" disabled={pending || !email}>
										{pending ? t("emailSubmitLoading") : t("emailSubmit")}
									</Button>
								</form>
							) : null}
							{methods.data?.google || methods.data?.microsoft ? (
								<div className="mt-4 flex gap-2">
									{methods.data?.google ? (
										<Tooltip>
											<TooltipTrigger asChild>
												<span className="flex flex-1">
													<Button
														variant="outline"
														className="flex-1"
														disabled={pending}
														onClick={() => social("google")}
													>
														<img src={googleLogo} alt="" className="size-5" />
													</Button>
												</span>
											</TooltipTrigger>
											<TooltipContent>{t("google")}</TooltipContent>
										</Tooltip>
									) : null}
									{methods.data?.microsoft ? (
										<Tooltip>
											<TooltipTrigger asChild>
												<span className="flex flex-1">
													<Button
														variant="outline"
														className="flex-1"
														disabled={pending}
														onClick={() => social("microsoft")}
													>
														<img src={microsoftLogo} alt="" className="size-5" />
													</Button>
												</span>
											</TooltipTrigger>
											<TooltipContent>{t("microsoft")}</TooltipContent>
										</Tooltip>
									) : null}
								</div>
							) : null}
							{(error || callbackError) && (
								<p role="alert" className="text-destructive text-center text-sm">
									{error ?? t("socialError")}
								</p>
							)}
						</CardContent>
					</>
				) : (
					<>
						<CardHeader className="text-center">
							<CardTitle className="text-2xl">{t("otpTitle")}</CardTitle>
							<CardDescription>{t("otpDescription", { email })}</CardDescription>
						</CardHeader>
						<CardContent>
							<form onSubmit={verify} className="flex flex-col items-center gap-4">
								<InputOTP
									autoFocus
									maxLength={6}
									disabled={pending}
									value={otp}
									onChange={setOtp}
									onComplete={() => verify()}
								>
									<InputOTPGroup>
										<InputOTPSlot index={0} />
										<InputOTPSlot index={1} />
										<InputOTPSlot index={2} />
										<InputOTPSlot index={3} />
										<InputOTPSlot index={4} />
										<InputOTPSlot index={5} />
									</InputOTPGroup>
								</InputOTP>
								<Button type="submit" className="w-full" disabled={pending || otp.length < 6}>
									{pending ? t("otpVerifyLoading") : t("otpVerify")}
								</Button>
								{error && (
									<p role="alert" className="text-destructive text-center text-sm">
										{error}
									</p>
								)}
								<div className="flex gap-4">
									<Button
										type="button"
										variant="ghost"
										disabled={pending}
										onClick={() => setStep("email")}
									>
										{t("otpBack")}
									</Button>
									<Button
										type="button"
										variant="link"
										disabled={pending || cooldown > 0}
										onClick={() => sendCode()}
									>
										{cooldown > 0 ? t("otpResendCooldown", { cooldown }) : t("otpResend")}
									</Button>
								</div>
							</form>
						</CardContent>
					</>
				)}
			</Card>
		</div>
	);
}
